import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as additionalQuestionsSchema } from './business-additional-questions-schema.js';
import { F as FormController } from './form.js';
import { A as AdditionalQuestions } from './Business.js';
import { B as BusinessFormStepV2 } from './business-form-types.js';
import { C as CURRENCY_MASK } from './form-input-masks.js';
import { b as businessServiceReceivedOptions, r as recurringPaymentsOptions, s as seasonalBusinessOptions } from './business-form-options.js';
import { d as defineCustomElement$7 } from './custom-popper2.js';
import { d as defineCustomElement$6 } from './form-control-error-text2.js';
import { d as defineCustomElement$5 } from './form-control-help-text2.js';
import { d as defineCustomElement$4 } from './form-control-monetary2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const AdditionalQuestionsFormStepCore = /*@__PURE__*/ proxyCustomElement(class AdditionalQuestionsFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.getData = () => {
            this.formLoading.emit(true);
            this.getBusiness({
                onSuccess: (response) => {
                    this.additional_questions = new AdditionalQuestions(response.data.additional_questions || {});
                    this.formController.setInitialValues(Object.assign({}, this.additional_questions));
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.formLoading.emit(false)
            });
        };
        this.sendData = (onSuccess) => {
            let submittedData;
            this.formLoading.emit(true);
            this.patchBusiness({
                payload: this.patchPayload,
                onSuccess: (response) => {
                    submittedData = response;
                    onSuccess();
                },
                onError: ({ error, code, severity }) => {
                    submittedData = error;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.submitted.emit({ data: { submittedData } });
                    this.stepCompleted.emit({ data: submittedData, formStep: BusinessFormStepV2.additionalQuestions });
                    this.formLoading.emit(false);
                }
            });
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
            this.additional_questions = new AdditionalQuestions(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.additional_questions = {};
        this.recurringPayments = false;
        this.getBusiness = undefined;
        this.patchBusiness = undefined;
        this.allowOptionalFields = undefined;
    }
    recurringPaymentsWatcher(newValue) {
        if ((newValue === null || newValue === void 0 ? void 0 : newValue.business_recurring_payments) === 'Yes') {
            this.recurringPayments = true;
        }
        else {
            this.recurringPayments = false;
        }
    }
    async validateAndSubmit({ onSuccess }) {
        this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    ;
    get patchPayload() {
        let formValues = new AdditionalQuestions(this.formController.values.getValue()).payload;
        return { additional_questions: formValues };
    }
    componentWillLoad() {
        this.getBusiness && this.getData();
        this.formController = new FormController(additionalQuestionsSchema(this.allowOptionalFields));
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g;
        const additionalQuestionsDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: '4a72792da50b0a4feaed0ced62266aa78b2e3c6b' }, h("fieldset", { key: 'de77f0e1933a7e5a1dc4c99906d4b4b5385f66ee' }, h("div", { key: '121f4a2ede83b5d91757c18bb1fb223a866470ea', class: "d-flex align-items-center gap-2" }, h("legend", { key: '15d0b9d2fc50e5bd5746be1d589e902feb2583ca', class: "mb-0" }, "Additional Questions"), h("form-control-tooltip", { key: 'e1babd3de64a0dd1c6daa70691e8d46183a8fd3e', helpText: "This information helps us understand the business and identify and monitor trends to safeguard you and your customers." })), h("hr", { key: '44ca37e8de71170488b58b4a4117e287e6ab0766', class: "mt-2" }), h("div", { key: '655521e5a3774d72ec026e9ca7309b680710b40d', class: "row gy-3" }, h("div", { key: 'bfd69518ce9c2efc1db13d0e8d708a995181a6cc', class: "col-12 col-md-6" }, h("form-control-monetary", { key: 'bcaac6bcc18fad927c42e3881e8ef21a3f6bc972', name: "business_revenue", label: "What is your business\u2019 estimated annual revenue from its primary business activities?", inputHandler: this.inputHandler, errorText: (_a = this.errors) === null || _a === void 0 ? void 0 : _a.business_revenue, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_revenue, maskOptions: CURRENCY_MASK.WHOLE })), h("div", { key: '1f1b0ae79efd5d58554cd88dbb04defa0d2bdc9b', class: "col-12 col-md-6" }, h("form-control-monetary", { key: 'f4f561cf3bea66969e310827c6c8a7d7b02a02ba', name: "business_payment_volume", label: "What is your business' annual credit card & ACH volume anticipated to process?", inputHandler: this.inputHandler, errorText: (_b = this.errors) === null || _b === void 0 ? void 0 : _b.business_payment_volume, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_payment_volume, maskOptions: CURRENCY_MASK.WHOLE })), h("div", { key: 'c23361e909ec2854a2289e7a361c9cb60019b46a', class: "col-12" }, h("form-control-select", { key: 'bda327ec9c7435c1cd27cfe4c5c29dfdb7245ec0', name: "business_when_service_received", label: "On average, how long after paying will your customers typically receive their goods or services?", inputHandler: this.inputHandler, errorText: (_c = this.errors) === null || _c === void 0 ? void 0 : _c.business_when_service_received, options: businessServiceReceivedOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_when_service_received })), h("div", { key: '27bcc5431a686e824615ae01dead1f5104c9b94a', class: "col-12" }, h("form-control-select", { key: '082d23acbbd37508d217cc21b7768117408d763f', name: "business_recurring_payments", label: "Does your business offer recurring payments?", inputHandler: this.inputHandler, errorText: (_d = this.errors) === null || _d === void 0 ? void 0 : _d.business_recurring_payments, options: recurringPaymentsOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_recurring_payments })), h("div", { key: '4167027883cdff3ad77a4079dd373fabf09d9ec5', class: 'col-12', hidden: !this.recurringPayments }, h("form-control-text", { key: 'f07a610b67e17d733288cdb1f848e03250aea598', name: "business_recurring_payments_percentage", label: "What percent of revenue is generated from each recurring payment type offered?", inputHandler: this.inputHandler, errorText: (_e = this.errors) === null || _e === void 0 ? void 0 : _e.business_recurring_payments_percentage, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_recurring_payments_percentage, helpText: "For example: 50% monthly, 50% annual." })), h("div", { key: '9baae4260f1cda1d43e594ce3a96461584e0a67e', class: "col-12" }, h("form-control-select", { key: 'a61245d3c61cc7de903bfdc5f3082065c58ce554', name: "business_seasonal", label: "Is this business seasonal?", inputHandler: this.inputHandler, errorText: (_f = this.errors) === null || _f === void 0 ? void 0 : _f.business_seasonal, options: seasonalBusinessOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_seasonal })), h("div", { key: 'd98b4071d9e70643d1808b913062e479b7d28cda', class: "col-12" }, h("form-control-text", { key: '5b7103e0fbc391dba09fd5cf0adebcfa827ddb14', name: "business_other_payment_details", label: "Is there anything else you would like us to know about how your customers pay the business? (optional)", inputHandler: this.inputHandler, errorText: (_g = this.errors) === null || _g === void 0 ? void 0 : _g.business_other_payment_details, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_other_payment_details }))))));
    }
    static get watchers() { return {
        "additional_questions": ["recurringPaymentsWatcher"]
    }; }
}, [0, "justifi-additional-questions-form-step-core", {
        "getBusiness": [16],
        "patchBusiness": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "additional_questions": [32],
        "recurringPayments": [32],
        "validateAndSubmit": [64]
    }, undefined, {
        "additional_questions": ["recurringPaymentsWatcher"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-additional-questions-form-step-core", "custom-popper", "form-control-error-text", "form-control-help-text", "form-control-monetary", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-additional-questions-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, AdditionalQuestionsFormStepCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-monetary":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { AdditionalQuestionsFormStepCore as A, defineCustomElement as d };
